/**
 * Utility functions for balance display logic with "I Owe / He Owes" labels
 */

export interface BalanceDisplay {
  label: string;
  color: 'success' | 'destructive';
  textColor: string;
  bgColor: string;
  isPositive: boolean;
}

/**
 * Get balance display configuration based on party type and balance
 * @param balance - The current balance (positive = they owe us, negative = we owe them)
 * @param partyType - 'customer' or 'vendor'
 * @returns Configuration object with label, colors, and state
 */
export const getBalanceDisplay = (
  balance: number,
  partyType: 'customer' | 'vendor'
): BalanceDisplay => {
  const isPositive = balance >= 0;

  if (partyType === 'customer') {
    return {
      label: isPositive ? 'He Owes' : 'I Owe',
      color: isPositive ? 'success' : 'destructive',
      textColor: isPositive ? 'text-[#00c46a]' : 'text-[#ff4d4d]',
      bgColor: isPositive ? 'bg-[#00c46a]/10' : 'bg-[#ff4d4d]/10',
      isPositive,
    };
  } else {
    // Vendor
    return {
      label: isPositive ? 'Receivable' : 'Payable',
      color: isPositive ? 'success' : 'destructive',
      textColor: isPositive ? 'text-[#00c46a]' : 'text-[#ff4d4d]',
      bgColor: isPositive ? 'bg-[#00c46a]/10' : 'bg-[#ff4d4d]/10',
      isPositive,
    };
  }
};

/**
 * Get balance type display for modal input
 * @param balanceType - 'receivable' or 'payable'
 * @param partyType - 'customer' or 'vendor'
 */
export const getBalanceTypeLabel = (
  balanceType: 'receivable' | 'payable',
  partyType: 'customer' | 'vendor'
): { label: string; color: string } => {
  if (partyType === 'customer') {
    return balanceType === 'receivable'
      ? { label: 'He Owes Me', color: 'text-[#00c46a]' }
      : { label: 'I Owe Him', color: 'text-[#ff4d4d]' };
  } else {
    return balanceType === 'receivable'
      ? { label: 'Vendor Owes Us', color: 'text-[#00c46a]' }
      : { label: 'We Owe Vendor', color: 'text-[#ff4d4d]' };
  }
};
